import { useEffect, useMemo, useState } from 'react';
import type { AppLanguage } from '../i18n/types';
import {
  buildMonthCalendarGrid,
  eachCalendarDayInRange,
  formatCalendarDayKeyLabel,
  formatCalendarDayTimeLabel,
  isPastCalendarDayKey,
  normalizeCalendarRangeKeys,
  rangeIncludesReservedDay,
  shiftMonth,
  todayCalendarDayKey,
} from '../utils/assetBookingCalendar';
import { LocaleTimeInput } from './LocaleTimeInput';
import styles from './AssetBookingCalendar.module.css';

export type CalendarPickMode = 'start' | 'end';

export type AssetBookingCalendarLabels = {
  title: string;
  hint: string;
  startDate: string;
  endDate: string;
  notSelected: string;
  pickStart: string;
  pickEnd: string;
  reservedLegend: string;
  rangeLegend: string;
  /** Closed / blackout days legend. */
  closedLegend?: string;
  prevMonth: string;
  nextMonth: string;
  loading: string;
  rangeBlocked: string;
  pickEndNeedsStart: string;
  startTime: string;
  endTime: string;
  hourlyHint: string;
  endTimeAuto: string;
  /** Single-day hourly: label for the chosen day row. */
  selectedDay?: string;
};

type AssetBookingCalendarProps = {
  reservedDayKeys: ReadonlySet<string>;
  /** Closed / blackout / weekday-shut days (not bookable). */
  isDayClosed?: (dayKey: string) => boolean;
  startDayKey: string | null;
  endDayKey: string | null;
  onStartDaySelect: (dayKey: string) => void;
  onEndDaySelect: (dayKey: string) => void;
  labels: AssetBookingCalendarLabels;
  language: AppLanguage;
  theme?: 'dark' | 'light';
  loading?: boolean;
  /** Hourly rate: show time inputs below the date summary. */
  hourly?: boolean;
  startTime?: string;
  endTime?: string;
  onStartTimeChange?: (time: string) => void;
  onEndTimeChange?: (time: string) => void;
  /** When false (hourly + period count), end time is read-only from computed end. */
  showEndTimePicker?: boolean;
  idPrefix?: string;
  /** Update booking: start is fixed; only end date is picked on the calendar. */
  lockStartPick?: boolean;
  /** Hourly same-day: pick one day only (no end-date range). */
  singleDayPick?: boolean;
  /** View-only calendar (e.g. hourly edit — change hours via period count). */
  disableDayPick?: boolean;
};

function weekdayShortLabels(language: AppLanguage): string[] {
  const locale = language === 'he' ? 'he-IL' : undefined;
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 7 + i)));
}

export function AssetBookingCalendar({
  reservedDayKeys,
  isDayClosed,
  startDayKey,
  endDayKey,
  onStartDaySelect,
  onEndDaySelect,
  labels,
  language,
  theme = 'dark',
  loading = false,
  hourly = false,
  startTime = '',
  endTime = '',
  onStartTimeChange,
  onEndTimeChange,
  showEndTimePicker = true,
  idPrefix = 'booking-cal',
  lockStartPick = false,
  singleDayPick = false,
  disableDayPick = false,
}: AssetBookingCalendarProps) {
  const startTimeId = `${idPrefix}-start-time`;
  const endTimeId = `${idPrefix}-end-time`;
  const todayKey = todayCalendarDayKey();
  const initialView = useMemo(() => {
    const seed = startDayKey ?? todayKey;
    const y = Number(seed.slice(0, 4));
    const m = Number(seed.slice(5, 7)) - 1;
    if (Number.isFinite(y) && Number.isFinite(m)) {
      return { year: y, monthIndex: m };
    }
    const now = new Date();
    return { year: now.getFullYear(), monthIndex: now.getMonth() };
  }, [startDayKey, todayKey]);

  const [viewYear, setViewYear] = useState(initialView.year);
  const [viewMonthIndex, setViewMonthIndex] = useState(initialView.monthIndex);
  const [pickMode, setPickMode] = useState<CalendarPickMode>(lockStartPick ? 'end' : 'start');
  const [rangeError, setRangeError] = useState<string | null>(null);

  useEffect(() => {
    if (lockStartPick) {
      setPickMode('end');
    }
  }, [lockStartPick]);

  const monthLabel = new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : undefined, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(viewYear, viewMonthIndex, 1));

  const weekdays = useMemo(() => weekdayShortLabels(language), [language]);
  const cells = useMemo(
    () => buildMonthCalendarGrid(viewYear, viewMonthIndex),
    [viewYear, viewMonthIndex],
  );

  const committedRange =
    singleDayPick && startDayKey
      ? { start: startDayKey, end: startDayKey }
      : startDayKey && endDayKey
        ? normalizeCalendarRangeKeys(startDayKey, endDayKey)
        : null;

  const startDisplay =
    startDayKey && hourly && startTime && !singleDayPick
      ? formatCalendarDayTimeLabel(startDayKey, startTime, language)
      : startDayKey
        ? formatCalendarDayKeyLabel(startDayKey, language)
        : labels.notSelected;
  const endDisplay =
    singleDayPick && endDayKey && endTime
      ? formatCalendarDayTimeLabel(endDayKey, endTime, language)
      : endDayKey && hourly && endTime
        ? formatCalendarDayTimeLabel(endDayKey, endTime, language)
        : endDayKey
          ? formatCalendarDayKeyLabel(endDayKey, language)
          : labels.notSelected;

  const rootClass = [styles.root, theme === 'light' ? styles.lightTheme : ''].filter(Boolean).join(' ');

  const onDayClick = (dayKey: string) => {
    if (
      disableDayPick ||
      loading ||
      isPastCalendarDayKey(dayKey) ||
      reservedDayKeys.has(dayKey) ||
      isDayClosed?.(dayKey)
    ) {
      return;
    }
    setRangeError(null);

    if (singleDayPick || pickMode === 'start') {
      onStartDaySelect(dayKey);
      return;
    }

    if (!startDayKey) {
      setRangeError(labels.pickEndNeedsStart);
      return;
    }

    const { start, end } = normalizeCalendarRangeKeys(startDayKey, dayKey);
    if (rangeIncludesReservedDay(start, end, reservedDayKeys)) {
      setRangeError(labels.rangeBlocked);
      return;
    }
    if (isDayClosed) {
      for (const key of eachCalendarDayInRange(start, end)) {
        if (isDayClosed(key)) {
          setRangeError(labels.rangeBlocked);
          return;
        }
      }
    }
    onEndDaySelect(dayKey);
  };

  const onPrevMonth = () => {
    const next = shiftMonth(viewYear, viewMonthIndex, -1);
    setViewYear(next.year);
    setViewMonthIndex(next.monthIndex);
  };

  const onNextMonth = () => {
    const next = shiftMonth(viewYear, viewMonthIndex, 1);
    setViewYear(next.year);
    setViewMonthIndex(next.monthIndex);
  };

  const timeSection =
    hourly ? (
      <div className={styles.timeSection} aria-label={labels.hourlyHint}>
        <p className={styles.timeHint}>{labels.hourlyHint}</p>
        <div className={styles.timeRow}>
          <label className={styles.timeField} htmlFor={startTimeId}>
            <span className={styles.timeFieldLabel}>{labels.startTime}</span>
            <LocaleTimeInput
              id={startTimeId}
              className={styles.timeInput}
              selectClassName={styles.timeInput}
              language={language}
              wholeHoursOnly
              ariaLabel={labels.startTime}
              value={startTime}
              disabled={!startDayKey || loading || lockStartPick}
              onChange={(next) => onStartTimeChange?.(next)}
            />
          </label>
          {showEndTimePicker ? (
            <label className={styles.timeField} htmlFor={endTimeId}>
              <span className={styles.timeFieldLabel}>{labels.endTime}</span>
              <LocaleTimeInput
                id={endTimeId}
                className={styles.timeInput}
                selectClassName={styles.timeInput}
                language={language}
                wholeHoursOnly
                ariaLabel={labels.endTime}
                value={endTime}
                disabled={!endDayKey || loading}
                onChange={(next) => onEndTimeChange?.(next)}
              />
            </label>
          ) : (
            <div className={styles.timeFieldReadonly}>
              <span className={styles.timeFieldLabel}>{labels.endTime}</span>
              <span className={styles.timeFieldValue}>
                {endDayKey && endTime
                  ? formatCalendarDayTimeLabel(endDayKey, endTime, language)
                  : labels.endTimeAuto}
              </span>
            </div>
          )}
        </div>
      </div>
    ) : null;

  return (
    <section className={rootClass} aria-label={labels.title}>
      <h3 className={styles.title}>{labels.title}</h3>

      {!singleDayPick ? (
        <div className={styles.dateSummary}>
          <div className={styles.dateSummaryRow}>
            <span className={styles.dateSummaryLabel}>{labels.startDate}</span>
            <span className={styles.dateSummaryValue}>{startDisplay}</span>
          </div>
          <div className={styles.dateSummaryRow}>
            <span className={styles.dateSummaryLabel}>{labels.endDate}</span>
            <span className={styles.dateSummaryValue}>{endDisplay}</span>
          </div>
        </div>
      ) : null}

      {!singleDayPick ? timeSection : null}

      {!singleDayPick ? (
        <div className={styles.pickModeRow} role="group" aria-label={labels.hint}>
          {lockStartPick ? (
            <button
              type="button"
              className={`${styles.pickModeButton} ${styles.pickModeButtonActive}`}
              aria-pressed
              disabled
            >
              {labels.pickEnd}
            </button>
          ) : (
            <>
              <button
                type="button"
                className={`${styles.pickModeButton}${pickMode === 'start' ? ` ${styles.pickModeButtonActive}` : ''}`}
                aria-pressed={pickMode === 'start'}
                onClick={() => {
                  setPickMode('start');
                  setRangeError(null);
                }}
              >
                {labels.pickStart}
              </button>
              <button
                type="button"
                className={`${styles.pickModeButton}${pickMode === 'end' ? ` ${styles.pickModeButtonActive}` : ''}`}
                aria-pressed={pickMode === 'end'}
                onClick={() => {
                  setPickMode('end');
                  setRangeError(null);
                }}
              >
                {labels.pickEnd}
              </button>
            </>
          )}
        </div>
      ) : null}

      <p className={styles.hint}>{labels.hint}</p>
      {rangeError ? (
        <p className={styles.rangeError} role="alert">
          {rangeError}
        </p>
      ) : null}

      <div className={styles.header}>
        <button type="button" className={styles.navButton} onClick={onPrevMonth} aria-label={labels.prevMonth}>
          ‹
        </button>
        <span className={styles.monthLabel}>{monthLabel}</span>
        <button type="button" className={styles.navButton} onClick={onNextMonth} aria-label={labels.nextMonth}>
          ›
        </button>
      </div>

      {loading ? (
        <p className={styles.statusLine} role="status">
          {labels.loading}
        </p>
      ) : null}

      <div className={styles.weekdayRow}>
        {weekdays.map((label) => (
          <span key={label} className={styles.weekday}>
            {label}
          </span>
        ))}
      </div>

      <div className={styles.grid}>
        {cells.map((cell) => {
          const closed = Boolean(isDayClosed?.(cell.dayKey));
          const disabled =
            isPastCalendarDayKey(cell.dayKey) || reservedDayKeys.has(cell.dayKey) || closed;
          const inCommittedRange =
            committedRange !== null &&
            cell.dayKey >= committedRange.start &&
            cell.dayKey <= committedRange.end;
          const isStart = startDayKey === cell.dayKey;
          const isEnd = !singleDayPick && endDayKey === cell.dayKey;

          const dayClass = [
            styles.dayButton,
            !cell.inMonth ? styles.dayOutside : '',
            disabled ? styles.dayDisabled : '',
            closed ? styles.dayClosed : '',
            reservedDayKeys.has(cell.dayKey) ? styles.dayReserved : '',
            inCommittedRange ? styles.dayInRange : '',
            isStart || isEnd ? styles.dayRangeEnd : '',
            (singleDayPick || pickMode === 'start') && isStart ? styles.dayPickTarget : '',
            pickMode === 'end' && isEnd ? styles.dayPickTarget : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={cell.dayKey}
              type="button"
              className={dayClass}
              disabled={disabled || loading}
              aria-pressed={isStart || isEnd || inCommittedRange}
              onClick={() => onDayClick(cell.dayKey)}
            >
              {cell.day}
            </button>
          );
        })}
      </div>

      {singleDayPick ? (
        <div className={styles.dateSummary}>
          <div className={styles.dateSummaryRow}>
            <span className={styles.dateSummaryLabel}>{labels.selectedDay ?? labels.startDate}</span>
            <span className={styles.dateSummaryValue}>{startDisplay}</span>
          </div>
          <div className={styles.dateSummaryRow}>
            <span className={styles.dateSummaryLabel}>{labels.endTime}</span>
            <span className={styles.dateSummaryValue}>{endDisplay}</span>
          </div>
        </div>
      ) : null}

      {singleDayPick ? timeSection : null}

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchReserved}`} aria-hidden />
          {labels.reservedLegend}
        </span>
        {labels.closedLegend ? (
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.legendSwatchClosed}`} aria-hidden />
            {labels.closedLegend}
          </span>
        ) : null}
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchRange}`} aria-hidden />
          {labels.rangeLegend}
        </span>
      </div>
    </section>
  );
}
